import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
// Note: We'll use window.dashjs which is set up in VideoDetails.js
// This ensures we use the same instance that's been patched

// Patch dash.js globally to suppress error code 34 (multiplexed representations)
// This must be done at module level before any dash.js instances are created
// Note: window.dashjs is set up in VideoDetails.js, so we'll patch it there or use it here
if (typeof window !== 'undefined' && window.dashjs && window.dashjs.dependencies) {
    const dependencies = window.dashjs.dependencies;
    
    // Patch Debug logger to suppress error code 34 messages
    // The error is logged by Debug.js before reaching error handlers
    // Try multiple approaches to patch Debug
    if (dependencies.Debug) {
        // Patch Debug class static methods if they exist
        if (dependencies.Debug.log && !dependencies.Debug.__logPatched) {
            const originalDebugLog = dependencies.Debug.log;
            dependencies.Debug.log = function() {
                const args = Array.from(arguments);
                const message = args.join(' ');
                if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                    return; // Suppress
                }
                return originalDebugLog.apply(this, arguments);
            };
            dependencies.Debug.__logPatched = true;
        }
        
        // Patch Debug prototype methods
        if (dependencies.Debug.prototype && !dependencies.Debug.prototype.__patchedForCode34) {
            const DebugPrototype = dependencies.Debug.prototype;
            
            // Patch all methods that might log
            const methodsToPatch = ['log', 'warn', 'error', 'info', 'debug'];
            methodsToPatch.forEach(methodName => {
                if (DebugPrototype[methodName]) {
                    const originalMethod = DebugPrototype[methodName];
                    DebugPrototype[methodName] = function() {
                        const args = Array.from(arguments);
                        const message = args.join(' ');
                        if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                            return; // Suppress
                        }
                        return originalMethod.apply(this, arguments);
                    };
                }
            });
            
            DebugPrototype.__patchedForCode34 = true;
        }
        
        // Also try to patch the Debug instance methods if Debug is a factory
        if (typeof dependencies.Debug === 'function') {
            const DebugFactory = dependencies.Debug;
            if (!DebugFactory.__patchedForCode34) {
                dependencies.Debug = function() {
                    const debugInstance = DebugFactory.apply(this, arguments);
                    if (debugInstance && debugInstance.log) {
                        const originalLog = debugInstance.log;
                        debugInstance.log = function() {
                            const args = Array.from(arguments);
                            const message = args.join(' ');
                            if (message && (message.includes('Multiplexed representations') || message.includes('code 34'))) {
                                return;
                            }
                            return originalLog.apply(this, arguments);
                        };
                    }
                    return debugInstance;
                };
                // Copy static properties
                Object.keys(DebugFactory).forEach(key => {
                    dependencies.Debug[key] = DebugFactory[key];
                });
                DebugFactory.__patchedForCode34 = true;
            }
        }
    }
    
    // As a last resort, patch console methods to filter out error code 34 messages
    // This is more aggressive but will catch all console logs
    if (!window.__consolePatchedForCode34) {
        const originalConsoleError = console.error;
        const originalConsoleWarn = console.warn;
        const originalConsoleLog = console.log;
        const originalConsoleInfo = console.info;
        const originalConsoleDebug = console.debug;
        
        const shouldSuppress = function() {
            const args = Array.from(arguments);
            const message = args.join(' ');
            return message && (
                message.includes('Multiplexed representations') || 
                message.includes('code 34') ||
                message.includes('[Stream] Multiplexed') ||
                (message.includes('[Stream]') && message.includes('not supported') && message.includes('DASH-AVC'))
            );
        };
        
        console.error = function() {
            if (shouldSuppress.apply(this, arguments)) {
                return; // Suppress
            }
            return originalConsoleError.apply(console, arguments);
        };
        
        console.warn = function() {
            if (shouldSuppress.apply(this, arguments)) {
                return; // Suppress
            }
            return originalConsoleWarn.apply(console, arguments);
        };
        
        console.log = function() {
            if (shouldSuppress.apply(this, arguments)) {
                return; // Suppress
            }
            return originalConsoleLog.apply(console, arguments);
        };
        
        console.info = function() {
            if (shouldSuppress.apply(this, arguments)) {
                return; // Suppress
            }
            return originalConsoleInfo.apply(console, arguments);
        };
        
        console.debug = function() {
            if (shouldSuppress.apply(this, arguments)) {
                return; // Suppress
            }
            return originalConsoleDebug.apply(console, arguments);
        };
        
        window.__consolePatchedForCode34 = true;
        console.log('Console methods patched to suppress multiplexed representation errors');
    }
    
    // Patch Stream class to suppress error code 34 at the source
    // The error "[Stream] Multiplexed representations are intentionally not supported" 
    // is thrown from the Stream class, so we need to patch it directly
    if (dependencies.Stream && dependencies.Stream.prototype) {
        const StreamPrototype = dependencies.Stream.prototype;
        if (!StreamPrototype.__patchedForCode34) {
            // Patch the activate method which is where the error is likely thrown
            const originalActivate = StreamPrototype.activate;
            if (originalActivate) {
                StreamPrototype.activate = function() {
                    try {
                        return originalActivate.apply(this, arguments);
                    } catch (error) {
                        // Suppress error code 34
                        if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                            // Suppress the error - don't log or throw
                            return;
                        }
                        // Re-throw other errors
                        throw error;
                    }
                };
            }
            
            // Patch the method that checks for multiplexed representations (likely the 'K' function at Stream.js:437)
            // We need to find and patch the method that throws the error
            const streamMethods = Object.getOwnPropertyNames(StreamPrototype);
            for (const methodName of streamMethods) {
                if (typeof StreamPrototype[methodName] === 'function') {
                    const originalMethod = StreamPrototype[methodName];
                    StreamPrototype[methodName] = function() {
                        try {
                            return originalMethod.apply(this, arguments);
                        } catch (error) {
                            if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                                // Suppress error code 34
                                return;
                            }
                            throw error;
                        }
                    };
                }
            }
            
            StreamPrototype.__patchedForCode34 = true;
        }
    }
    
    // Patch EventBus to suppress error code 34
    if (dependencies.EventBus && dependencies.EventBus.prototype) {
        const EventBusPrototype = dependencies.EventBus.prototype;
        const originalTrigger = EventBusPrototype.trigger;
        if (originalTrigger && !EventBusPrototype.__patchedForCode34) {
            EventBusPrototype.trigger = function(event, data) {
                if (event === 'error' && data && data.error && data.error.code === 34) {
                    console.warn('Multiplexed representation error (code 34) suppressed via EventBus in DashPlayer');
                    return; // Suppress the error
                }
                return originalTrigger.apply(this, arguments);
            };
            EventBusPrototype.__patchedForCode34 = true;
        }
    }
    
    // Patch ErrorHandler to suppress error code 34
    if (dependencies.ErrorHandler && dependencies.ErrorHandler.prototype) {
        const ErrorHandlerPrototype = dependencies.ErrorHandler.prototype;
        const originalError = ErrorHandlerPrototype.error;
        if (originalError && !ErrorHandlerPrototype.__patchedForCode34) {
            ErrorHandlerPrototype.error = function(error) {
                if (error && (error.code === 34 || (error.message && error.message.includes('Multiplexed representations')))) {
                    console.warn('Multiplexed representation error (code 34) suppressed via ErrorHandler in DashPlayer');
                    return; // Suppress the error
                }
                return originalError.apply(this, arguments);
            };
            ErrorHandlerPrototype.__patchedForCode34 = true;
        }
    }
}

const DashPlayer = forwardRef(({ url, onReady, onError, onPlay, onPause, onEnded, tracks }, ref) => {
    const videoRef = useRef(null);
    const dashPlayerInstanceRef = useRef(null);

    useEffect(() => {
        if (!url || !videoRef.current) return;
        
        // Create dash.js player with configuration to allow multiplexed representations
        // Use window.dashjs which is set up in VideoDetails.js (already patched)
        const dashjsLib = window.dashjs;
        
        if (!dashjsLib) {
            console.error('dashjs library not available');
            if (onError) {
                onError(new Error('dash.js library not available'));
            }
            return;
        }
        
        // Log the structure for debugging
        console.log('dashjs structure:', {
            hasMediaPlayer: !!dashjsLib.MediaPlayer,
            keys: Object.keys(dashjsLib).slice(0, 20), // First 20 keys
            type: typeof dashjsLib
        });
        
        let player = null;
        try {
            // dashjs v5 API: MediaPlayer is a factory function
            // Try different access patterns
            let MediaPlayerFactory = null;
            
            if (dashjsLib.MediaPlayer) {
                MediaPlayerFactory = dashjsLib.MediaPlayer;
            } else if (dashjsLib.default && dashjsLib.default.MediaPlayer) {
                MediaPlayerFactory = dashjsLib.default.MediaPlayer;
            } else if (typeof dashjsLib === 'function') {
                // If dashjs itself is a function, it might be MediaPlayer
                MediaPlayerFactory = dashjsLib;
            }
            
            if (!MediaPlayerFactory) {
                throw new Error('dashjs.MediaPlayer not found. Available keys: ' + Object.keys(dashjsLib).join(', '));
            }
            
            // Create the player instance
            player = MediaPlayerFactory().create();
            
        } catch (createError) {
            console.error('Error creating dash.js player:', createError);
            console.error('Full dashjs object:', dashjsLib);
            if (onError) {
                onError(createError);
            }
            return;
        }
        
        if (!player) {
            console.error('Failed to create dash.js player');
            if (onError) {
                onError(new Error('Failed to create dash.js player'));
            }
            return;
        }
        
        // Note: Error code 34 suppression is handled by the patches at the module level
        // dashjs v5 has a different settings API, so we don't configure unsupported settings
        // The player will work with default settings, and the patches will suppress error code 34

        // Intercept error events to suppress error code 34
        // This must be set up before initialize() is called
        // Set up multiple error handlers to catch error code 34
        const errorHandler = (errorEvent) => {
            if (errorEvent && errorEvent.error && errorEvent.error.code === 34) {
                console.warn('Multiplexed representation error (code 34) suppressed in direct dash.js player');
                // Suppress the error - don't call onError
                // Don't stop playback - let it continue
                return;
            }
            // For other errors, call the onError handler
            if (onError) {
                onError(errorEvent);
            }
        };
        
        // Register error handler multiple ways to ensure we catch it
        if (player.on) {
            player.on('error', errorHandler);
        }
        if (player.addEventListener) {
            player.addEventListener('error', errorHandler);
        }
        
        // Also listen on the video element
        if (videoRef.current) {
            videoRef.current.addEventListener('error', (e) => {
                // Check if this is related to error code 34
                if (e.target && e.target.error) {
                    // Video element errors don't have code 34, but we can check the message
                    console.log('Video element error:', e.target.error);
                }
            });
        }

        // Before initializing, ensure Stream class is patched to prevent error code 34 from stopping playback
        // This is critical - the error is thrown synchronously and stops initialization
        if (dashjsLib.dependencies && dashjsLib.dependencies.Stream && dashjsLib.dependencies.Stream.prototype) {
            const StreamPrototype = dashjsLib.dependencies.Stream.prototype;
            
            // Wrap ALL methods in Stream prototype to catch error code 34
            const streamMethodNames = Object.getOwnPropertyNames(StreamPrototype);
            streamMethodNames.forEach(methodName => {
                if (typeof StreamPrototype[methodName] === 'function' && 
                    !StreamPrototype[methodName].__patchedForCode34 &&
                    methodName !== 'constructor') {
                    const originalMethod = StreamPrototype[methodName];
                    StreamPrototype[methodName] = function() {
                        try {
                            return originalMethod.apply(this, arguments);
                        } catch (error) {
                            if (error && (error.code === 34 || 
                                (error.message && error.message.includes('Multiplexed representations')))) {
                                // Suppress error code 34 - don't throw, just return undefined
                                console.warn(`Stream.${methodName}: Suppressed error code 34, continuing`);
                                return undefined;
                            }
                            throw error;
                        }
                    };
                    StreamPrototype[methodName].__patchedForCode34 = true;
                    // Preserve original method reference
                    StreamPrototype[methodName].__original = originalMethod;
                }
            });
            
            console.log('Stream class methods patched to suppress error code 34');
        }

        // Wait for video element to be ready before initializing
        const initPlayer = async () => {
            try {
                // Ensure video element is ready
                if (!videoRef.current) {
                    throw new Error('Video element not available');
                }
                
                // Clear any existing source
                videoRef.current.src = '';
                videoRef.current.load();
                
                // Wait a moment for video element to reset
                await new Promise((resolve) => setTimeout(resolve, 100));
                
                // Check if video element supports the format
                if (videoRef.current.canPlayType && !videoRef.current.canPlayType('application/dash+xml')) {
                    console.warn('Browser may not support DASH format natively, but dash.js should handle it');
                }
                
                // Set up error handler before initialization to catch format errors
                const formatErrorHandler = (errorEvent) => {
                    if (errorEvent && errorEvent.error) {
                        const error = errorEvent.error;
                        // Check for format error (code 4)
                        if (error.code === 4 || (error.message && error.message.includes('Format error'))) {
                            console.warn('Format error detected, but attempting to continue...');
                            // Don't stop - let dash.js try to handle it
                            return;
                        }
                    }
                };
                
                // Listen for errors on the video element
                videoRef.current.addEventListener('error', (e) => {
                    if (e.target && e.target.error) {
                        const mediaError = e.target.error;
                        if (mediaError.code === 4) {
                            console.warn('Video element format error (code 4) - this may be due to codec support or CORS');
                            // Try to reload after a delay
                            setTimeout(() => {
                                if (videoRef.current && dashPlayerInstanceRef.current) {
                                    try {
                                        console.log('Attempting to reinitialize player after format error...');
                                        dashPlayerInstanceRef.current.attachView(videoRef.current);
                                        dashPlayerInstanceRef.current.attachSource(url);
                                    } catch (reinitError) {
                                        console.error('Could not reinitialize:', reinitError);
                                    }
                                }
                            }, 2000);
                        }
                    }
                });
                
                // Initialize the player - pass false for autoplay to avoid immediate playback issues
                try {
                    player.initialize(videoRef.current, url, false);
                } catch (initErr) {
                    console.error('Error during player.initialize:', initErr);
                    throw initErr;
                }
                
                console.log('DASH player initialized successfully');
                
                // Wait a moment for initialization to complete
                await new Promise((resolve) => setTimeout(resolve, 500));
                
                return true;
            } catch (initError) {
                // If initialization fails due to error code 34, try to continue anyway
                if (initError && (initError.code === 34 || (initError.message && initError.message.includes('Multiplexed')))) {
                    console.warn('DASH initialization error code 34 caught, attempting to continue...');
                    return true;
                } else {
                    console.error('DASH player initialization error:', initError);
                    if (onError) {
                        onError(initError);
                    }
                    return false;
                }
            }
        };
        
        // Initialize and handle the result
        initPlayer().then((success) => {
            if (success) {
                // Wait a bit for the player to process the manifest
                setTimeout(() => {
                    // Check if video is actually loading
                    if (videoRef.current) {
                        const readyState = videoRef.current.readyState;
                        console.log('Video readyState after init:', readyState);
                        
                        // Check for video element errors
                        if (videoRef.current.error) {
                            console.error('Video element error:', {
                                code: videoRef.current.error.code,
                                message: videoRef.current.error.message
                            });
                            
                            // If it's a format error, try to reload
                            if (videoRef.current.error.code === 4) {
                                console.log('Format error detected, attempting to reload...');
                                setTimeout(() => {
                                    try {
                                        videoRef.current.load();
                                    } catch (e) {
                                        console.warn('Could not reload video:', e);
                                    }
                                }, 1000);
                            }
                        }
                    }
                }, 1000);
            }
        }).catch((err) => {
            console.error('Failed to initialize player:', err);
            if (onError) {
                onError(err);
            }
        });

        // Store the player instance
        dashPlayerInstanceRef.current = player;
        videoRef.current.__dashjs = player;

        // Call onReady callback
        if (onReady) {
            setTimeout(() => {
                onReady({
                    getInternalPlayer: () => videoRef.current,
                    player: { dashjs: player }
                });
            }, 100);
        }

        // Set up event listeners
        if (videoRef.current) {
            videoRef.current.addEventListener('play', () => {
                if (onPlay) onPlay();
            });
            videoRef.current.addEventListener('pause', () => {
                if (onPause) onPause();
            });
            videoRef.current.addEventListener('ended', () => {
                if (onEnded) onEnded();
            });
        }

        // Cleanup
        return () => {
            if (dashPlayerInstanceRef.current) {
                try {
                    dashPlayerInstanceRef.current.destroy();
                } catch (e) {
                    console.warn('Error destroying dash.js player:', e);
                }
                dashPlayerInstanceRef.current = null;
            }
        };
    }, [url, onReady, onError, onPlay, onPause, onEnded]);

    // Expose player methods through ref
    useImperativeHandle(ref, () => ({
        getInternalPlayer: () => videoRef.current,
        player: { dashjs: dashPlayerInstanceRef.current }
    }), []);

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            <video
                ref={videoRef}
                controls
                style={{ width: '100%', height: 'auto' }}
                crossOrigin="anonymous"
                playsInline
                preload="metadata"
            >
                {tracks && tracks.map((track, index) => (
                    <track
                        key={index}
                        kind={track.kind}
                        src={track.src}
                        srcLang={track.srcLang}
                        label={track.label}
                        default={track.default || false}
                    />
                ))}
            </video>
        </div>
    );
});

DashPlayer.displayName = 'DashPlayer';

export default DashPlayer;

